/**
 * La scheda di un candidato sul telefono (7/10/2026): risponde a "chi è, a
 * che punto è del percorso, e come lo raggiungo?".
 *
 * Prima, a 390px, era la pagina del computer stretta: tre numeri in griglia,
 * due tabelle larghe (corsi, placement) e la fase in una pastiglia in fondo
 * a destra, da cambiare con un pannello di pillole.
 * Qui:
 *   1. iniziali, nome, cosa sa fare e dove sta;
 *   2. i gesti: chiama, scrivi, WhatsApp;
 *   3. la fase del percorso, che si cambia toccando la riga (le stesse fasi
 *      del computer, in ordine), e la disponibilità;
 *   4. i corsi con le presenze, i placement, le note.
 * Eliminare il candidato sta nel menu ⋯ in alto, come sul computer. Stessa
 * chiamata e stessa cache del computer.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { MobileSheet } from '../../components/ui/MobileSheet';
import { PageActions } from '../../components/layout/PageActions';
import { MActions, MEmpty, MGroup, MIdentity, MPage, MRow, MScelte, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { CandidateStage } from '@masamune/shared';
import { FASE_CANDIDATO } from './TalentMobile';
import './talent-mobile.css';

interface CandidateDetail {
  id: string; firstName: string; lastName: string; email: string | null; phone: string | null; city: string | null;
  skill: string | null; seniority: string | null; availability: string | null; stage: CandidateStage; notes: string | null;
  enrollments: Array<{
    id: string; finalGrade: string | null; finalScore: number | null;
    course: { id: string; name: string; type: string; status: string };
    attendances: Array<{ status: string; lesson: { date: string; topic: string } }>;
  }>;
  placements: Array<{ id: string; status: string; position: string | null; startDate: string | null; client: { id: string; legalName: string } | null }>;
}

/** "333 123 4567" → "393331234567" per WhatsApp; i fissi (con lo 0) no. */
function numeroWhatsApp(tel: string | null | undefined): string | null {
  if (!tel) return null;
  let n = tel.replace(/[^\d+]/g, '');
  if (n.startsWith('+')) n = n.slice(1);
  else if (n.startsWith('00')) n = n.slice(2);
  else if (n.startsWith('3') && n.length === 10) n = `39${n}`;
  if (n.startsWith('390')) return null;
  return n.length >= 10 ? n : null;
}

const giorno = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '') : null);
const faseDi = (s: string) => FASE_CANDIDATO.find(([k]) => k === s)?.[1] ?? s.replace(/_/g, ' ').toLowerCase();
/** Il nome della fase come stato di una persona ("In screening", "Iscritto a un corso"). */
const FASE_SINGOLARE: Record<string, string> = {
  NUOVO_CANDIDATO: 'Nuovo candidato', SCREENING: 'In screening', IDONEO: 'Idoneo', ISCRITTO_AL_CORSO: 'Iscritto a un corso',
  FREQUENTANTE: 'Sta frequentando', PRONTO_PER_PLACEMENT: "Pronto per un'azienda", PROPOSTO_AD_AZIENDA: "Proposto a un'azienda",
  COLLOQUIO_FISSATO: 'Ha un colloquio fissato', SOMMINISTRATO: 'Somministrato', ASSUNTO: 'Assunto', SCARTATO: 'Scartato',
};

export function CandidatoMobile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [fase, setFase] = useState(false);
  const [nota, setNota] = useState(false);

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ['candidate', id],
    queryFn: () => api.get<CandidateDetail>(`/candidates/${id}`),
    enabled: !!id,
    retry: false,
  });

  // Lo stesso spostamento del computer: subito a schermo, poi il server conferma.
  const move = useMutation({
    mutationFn: (stage: CandidateStage) => api.post(`/candidates/${id}/move`, { stage }),
    onMutate: async (stage) => {
      await qc.cancelQueries({ queryKey: ['candidate', id] });
      const prev = qc.getQueryData<CandidateDetail>(['candidate', id]);
      if (prev) qc.setQueryData<CandidateDetail>(['candidate', id], { ...prev, stage });
      return { prev };
    },
    onSuccess: (_d, stage) => toast.ok('Fase aggiornata', FASE_SINGOLARE[stage] ?? stage),
    onError: (_e, _s, ctx) => {
      if (ctx?.prev) qc.setQueryData(['candidate', id], ctx.prev);
      toast.alert('Fase non aggiornata', 'Riprova tra un momento.');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['candidate', id] });
      qc.invalidateQueries({ queryKey: ['candidates'] });
    },
  });
  const del = useMutation({
    mutationFn: () => api.delete(`/candidates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['candidates'] });
      toast.ok('Candidato eliminato');
      navigate('/talent?p=academy&s=pool');
    },
    onError: (e: any) => toast.alert('Candidato non eliminato', e?.message ?? 'Riprova'),
  });

  if (isError) return <MPage><MEmpty>Candidato non trovato: forse è stato eliminato, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !c) return <MPage><MEmpty>Carico il candidato…</MEmpty></MPage>;

  const nome = `${c.firstName} ${c.lastName}`;
  const wa = numeroWhatsApp(c.phone);
  const lezioni = c.enrollments.reduce((a, e) => a + e.attendances.length, 0);
  const presenze = c.enrollments.reduce((a, e) => a + e.attendances.filter((x) => x.status === 'PRESENTE' || x.status === 'RITARDO').length, 0);

  return (
    <>
      <PageActions className="flex gap-2">
        <ActionsMenu
          variant="ghost"
          label="Azioni"
          items={[{
            key: 'delete', label: 'Elimina il candidato', icon: 'trash', danger: true,
            onClick: async () => { if (await confirmDialog({ title: 'Elimina candidato', body: `Eliminare ${nome}?`, danger: true, confirmLabel: 'Elimina' })) del.mutate(); },
          }]}
        />
      </PageActions>

      <div className="tlm">
        <MPage>
          <MIdentity
            text={iniziali(nome)}
            title={nome}
            sub={[c.skill, c.seniority, c.city ? leggibile(c.city) : null].filter(Boolean).join(' · ') || undefined}
          />

          <MActions
            azioni={[
              { label: 'Chiama', icon: 'phone', href: c.phone ? `tel:${c.phone.replace(/\s+/g, '')}` : null },
              { label: 'Scrivi', icon: 'mail', href: c.email ? `mailto:${c.email}` : null },
              { label: 'WhatsApp', icon: 'message', href: wa ? `https://wa.me/${wa}` : null },
            ]}
          />

          <MGroup title="A che punto è">
            <MRow
              icon="talent"
              tone={c.stage === CandidateStage.SCARTATO ? 'alert' : undefined}
              title="Fase"
              trail={FASE_SINGOLARE[c.stage] ?? faseDi(c.stage)}
              trailTone="mute"
              onClick={() => setFase(true)}
            />
            {c.availability && <MRow icon="calendar" title="Disponibilità" trail={c.availability} trailTone="mute" />}
            {lezioni > 0 && <MRow icon="check" title="Presenze alle lezioni" sub={`${presenze} su ${lezioni}`} trail={`${Math.round((presenze / lezioni) * 100)}%`} />}
          </MGroup>

          <MGroup title="Corsi" count={c.enrollments.length || undefined}>
            {c.enrollments.length ? c.enrollments.map((e) => (
              <MRow
                key={e.id}
                icon="talent"
                title={e.course.name}
                sub={[e.course.type.charAt(0) + e.course.type.slice(1).toLowerCase(), `${e.attendances.length} lezioni`, e.finalGrade ? `voto ${e.finalGrade}` : null].filter(Boolean).join(' · ')}
                onClick={() => navigate(`/courses/${e.course.id}`)}
              />
            )) : <MEmpty>Non è iscritto a nessun corso.</MEmpty>}
          </MGroup>

          <MGroup title="Placement" count={c.placements.length || undefined}>
            {c.placements.length ? c.placements.map((p) => (
              <MRow
                key={p.id}
                icon="building"
                title={p.client ? leggibile(p.client.legalName) : 'Azienda non indicata'}
                sub={[p.position, p.startDate ? `dal ${giorno(p.startDate)}` : null, p.status.replace(/_/g, ' ').toLowerCase()].filter(Boolean).join(' · ')}
                onClick={p.client ? () => navigate(`/clients/${p.client!.id}`) : undefined}
              />
            )) : <MEmpty>Nessun placement, per ora.</MEmpty>}
          </MGroup>

          {(c.email || c.phone || c.notes) && (
            <MGroup title="Recapiti e note">
              {c.phone && <MRow icon="phone" title={c.phone} sub="telefono" />}
              {c.email && <MRow icon="mail" title={c.email} sub="email" />}
              {c.notes && <MRow icon="pencil" title="Note" sub={c.notes.split('\n')[0]} onClick={() => setNota(true)} />}
            </MGroup>
          )}
        </MPage>
      </div>

      <MScelte
        open={fase}
        onClose={() => setFase(false)}
        titolo="Fase del percorso"
        opzioni={FASE_CANDIDATO.map(([k]) => ({ v: k, l: FASE_SINGOLARE[k] ?? k }))}
        valore={c.stage}
        onScegli={(v) => { setFase(false); if (v !== c.stage) move.mutate(v as CandidateStage); }}
      />
      <MobileSheet open={nota} onClose={() => setNota(false)} title="Note">
        <div className="tlm-testo">{c.notes}</div>
      </MobileSheet>
    </>
  );
}
